import { sanitizeCsvValue, sanitizeObjectsForCSV } from "src/utils/csvSanitizeUtils";
import { deriveDisplayValue } from "./derivations/valueDerivations";
import type { ClaimRecord, ExportConfig, ExportField } from "./types";

/** One CSV column: the label its header cell shows, and the key its data cells sit under. */
export interface CsvExportHeader {
  readonly label: string;
  readonly key: string;
}

/** Everything the export control needs to hand to `CSVLink`, and nothing it has to derive itself. */
export interface CsvExport {
  readonly headers: readonly CsvExportHeader[];
  readonly rows: readonly Record<string, string>[];
  readonly fileName: string;
}

/**
 * Keyed on the field's declared position rather than its `source`, so two
 * fields naming the same property still occupy separate columns. Keying on
 * `source` would silently collapse them into one and drop a declared column
 * from the file. The key never reaches the user; only `label` does.
 */
function headerKeyFor(index: number): string {
  return `field_${index}`;
}

/**
 * Rejects a config that cannot produce a complete file. This module is
 * permitted to throw, unlike the derivations: a CSV missing a declared column
 * or carrying a nameless header is worse than no CSV, because a user who
 * receives a file assumes it is complete.
 */
function readUsableFields(exportConfig: ExportConfig): readonly ExportField[] {
  if (typeof exportConfig !== "object" || exportConfig === null) {
    throw new TypeError("Export config is absent.");
  }
  const fields = exportConfig.fields;
  if (!Array.isArray(fields) || fields.length === 0) {
    throw new TypeError("Export config declares no fields.");
  }
  fields.forEach((field: ExportField, index: number) => {
    if (typeof field !== "object" || field === null) {
      throw new TypeError(`Export field ${index} is absent.`);
    }
    if (typeof field.label !== "string" || field.label === "") {
      throw new TypeError(`Export field ${index} declares no label.`);
    }
    if (typeof field.source !== "string" || field.source === "") {
      throw new TypeError(`Export field ${index} declares no source.`);
    }
  });
  return fields;
}

function readFileNamePrefix(exportConfig: ExportConfig): string {
  const prefix = exportConfig.fileNamePrefix;
  if (typeof prefix !== "string" || prefix === "") {
    throw new TypeError("Export config declares no file name prefix.");
  }
  return prefix;
}

/**
 * `toISOString` is already ISO 8601 expressed in UTC, so the legacy button's
 * `moment().toISOString()` is not needed to produce the same string.
 */
function buildFileName(prefix: string): string {
  return `${prefix}_${new Date().toISOString()}.csv`;
}

/**
 * The declared export fields of one claim, keyed for the header row. Cells come
 * from the same `deriveDisplayValue` the table column uses, so an exported cell
 * cannot diverge from the displayed one, and a field the config does not
 * declare contributes nothing.
 */
function buildDataRow(fields: readonly ExportField[], claim: ClaimRecord): Record<string, unknown> {
  const row: Record<string, unknown> = {};
  fields.forEach((field, index) => {
    row[headerKeyFor(index)] = deriveDisplayValue(field, claim);
  });
  return row;
}

/**
 * A CSV of `claims` as the Export_Config declares it: one header cell per
 * declared field in declared order, one data row per claim in the order passed
 * in, which is the table's active sort order.
 *
 * Throws rather than returning a partial export. The caller reports that the
 * export did not complete and leaves the table untouched.
 */
export function buildCsvExport(
  exportConfig: ExportConfig,
  claims: readonly ClaimRecord[]
): CsvExport {
  const fields = readUsableFields(exportConfig);
  const fileName = buildFileName(readFileNamePrefix(exportConfig));

  if (!Array.isArray(claims)) {
    throw new TypeError("Export received no claims to write.");
  }

  // Labels are sanitized like the data cells rather than written raw. The
  // validator bounds a label's length but not its first character, so a label
  // such as "=cmd" would otherwise reach the file as a live formula. Nothing in
  // the shipped configs starts with a trigger character, so this is invisible
  // today and protective once these documents are served from a backend.
  const headers = fields.map((field, index) => ({
    label: sanitizeCsvValue(field.label),
    key: headerKeyFor(index),
  }));

  const dataRows = claims.map((claim) => buildDataRow(fields, claim));

  return { headers, rows: sanitizeObjectsForCSV(dataRows), fileName };
}
